import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent, Button } from '../components/ui';
import { api } from '../lib/api';

interface FreshnessBreakdown {
  current: number;
  needs_review: number;
  stale: number;
  unverified: number;
  total: number;
}

interface HealthStats {
  properties: FreshnessBreakdown;
  transactions: FreshnessBreakdown;
  market_intel: FreshnessBreakdown;
  overall_health_percent: number;
  total_records: number;
}

interface PoolStats {
  total_properties: number;
  total_transactions: number;
  total_comp_links: number;
  total_market_intel: number;
  total_contacts: number;
  sources: Record<string, number>;
}

interface VerifRecord {
  id: string;
  address: string;
  city?: string;
  state?: string;
  property_type?: string;
  building_size?: number;
  submarket?: string;
  freshness_status: string;
  verified_at?: string;
  updated_at: string;
}

const FRESHNESS_COLORS: Record<string, { bg: string; text: string; bar: string }> = {
  current: { bg: 'bg-success-wash', text: 'text-success', bar: 'bg-success' },
  needs_review: { bg: 'bg-warning-wash', text: 'text-warning', bar: 'bg-warning' },
  stale: { bg: 'bg-red-50', text: 'text-red-700', bar: 'bg-red-500' },
  unverified: { bg: 'bg-gray-50', text: 'text-gray-500', bar: 'bg-gray-400' },
};

function FreshnessBar({ breakdown, label }: { breakdown: FreshnessBreakdown; label: string }) {
  if (breakdown.total === 0) return null;
  const pct = (n: number) => Math.round((n / breakdown.total) * 100);
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-xs text-gray-500">{breakdown.total} records</span>
      </div>
      <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden flex">
        {breakdown.current > 0 && <div className="bg-success h-full" style={{ width: `${pct(breakdown.current)}%` }} title={`Current: ${breakdown.current}`} />}
        {breakdown.needs_review > 0 && <div className="bg-warning h-full" style={{ width: `${pct(breakdown.needs_review)}%` }} title={`Needs Review: ${breakdown.needs_review}`} />}
        {breakdown.stale > 0 && <div className="bg-red-500 h-full" style={{ width: `${pct(breakdown.stale)}%` }} title={`Stale: ${breakdown.stale}`} />}
        {breakdown.unverified > 0 && <div className="bg-gray-400 h-full" style={{ width: `${pct(breakdown.unverified)}%` }} title={`Unverified: ${breakdown.unverified}`} />}
      </div>
      <div className="flex gap-4 mt-1 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-success" />{breakdown.current} current</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-warning" />{breakdown.needs_review} needs review</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />{breakdown.stale} stale</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400" />{breakdown.unverified} unverified</span>
      </div>
    </div>
  );
}

export default function DataHealth() {
  const [health, setHealth] = useState<HealthStats | null>(null);
  const [pool, setPool] = useState<PoolStats | null>(null);
  const [queue, setQueue] = useState<VerifRecord[]>([]);
  const [queueTotal, setQueueTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recomputing, setRecomputing] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'queue'>('overview');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [healthData, poolData, queueData] = await Promise.all([
        api.getDataHealth(),
        api.getPoolStats(),
        api.getVerificationQueue({ limit: '20' }),
      ]);
      setHealth(healthData);
      setPool(poolData.pool_stats);
      setQueue(queueData.records || []);
      setQueueTotal(queueData.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load data health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      await api.recomputeFreshness();
      await fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setRecomputing(false);
    }
  };

  const healthColor = (pct: number) => {
    if (pct >= 70) return 'text-success';
    if (pct >= 40) return 'text-warning';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-20 text-gray-500">Loading data health dashboard...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Data Health</h1>
            <p className="mt-1 text-sm text-gray-500">Monitor the freshness and completeness of your proprietary data pool</p>
          </div>
          <Button onClick={handleRecompute} disabled={recomputing}>
            {recomputing ? 'Recomputing...' : 'Recompute Freshness'}
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        {/* Pool Stats Cards */}
        {pool && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{pool.total_properties.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Properties</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{pool.total_transactions.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Transactions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{pool.total_comp_links.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Comp Links</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{pool.total_market_intel.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Market Intel</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{pool.total_contacts.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">Contacts</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Overall Health Score */}
        {health && (
          <Card>
            <CardContent className="py-6">
              <div className="text-center">
                <p className={`text-5xl font-bold ${healthColor(health.overall_health_percent)}`}>
                  {health.overall_health_percent}%
                </p>
                <p className="text-sm text-gray-500 mt-2">Overall Data Health</p>
                <p className="text-xs text-gray-400 mt-1">{health.total_records.toLocaleString()} total records across all entities</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            <button onClick={() => setActiveTab('overview')} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'overview' ? 'border-primary-500 text-primary-500' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Freshness Overview</button>
            <button onClick={() => setActiveTab('queue')} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'queue' ? 'border-primary-500 text-primary-500' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              Verification Queue {queueTotal > 0 && <span className="ml-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">{queueTotal}</span>}
            </button>
          </nav>
        </div>

        {activeTab === 'overview' && health && (
          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle>Freshness by Entity Type</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <FreshnessBar breakdown={health.properties} label="Properties" />
                <FreshnessBar breakdown={health.transactions} label="Transactions" />
                <FreshnessBar breakdown={health.market_intel} label="Market Intel" />
              </CardContent>
            </Card>

            {/* Source Breakdown */}
            {pool && Object.keys(pool.sources).length > 0 && (
              <Card>
                <CardHeader><CardTitle>Data Sources</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {Object.entries(pool.sources).sort((a, b) => b[1] - a[1]).map(([source, count]) => (
                      <div key={source} className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-lg font-semibold text-gray-900">{count.toLocaleString()}</p>
                        <p className="text-xs text-gray-500 capitalize">{source.replace(/_/g, ' ')}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'queue' && (
          <Card>
            <CardHeader>
              <CardTitle>Records Needing Review ({queueTotal})</CardTitle>
            </CardHeader>
            <CardContent>
              {queue.length === 0 ? (
                <p className="text-gray-500 text-center py-8">All records are current. Nice work!</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-2 font-medium text-gray-600">Address</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600">City</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600">Type</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600">Status</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600">Last Verified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {queue.map(record => {
                        const colors = FRESHNESS_COLORS[record.freshness_status] || FRESHNESS_COLORS.unverified;
                        return (
                          <tr key={record.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-2 font-medium">
                              <Link to={`/data-hub/${record.id}`} className="text-primary-500 hover:underline">{record.address}</Link>
                            </td>
                            <td className="py-3 px-2 text-gray-600">{record.city}{record.state ? `, ${record.state}` : ''}</td>
                            <td className="py-3 px-2 text-gray-600 capitalize">{record.property_type?.replace(/_/g, ' ') || '—'}</td>
                            <td className="py-3 px-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                                {record.freshness_status}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-gray-500">
                              {record.verified_at ? new Date(record.verified_at).toLocaleDateString() : 'Never'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
